// lib/teamAPI.ts
// Wraps the Teams endpoints using the shared `api` axios instance,
// which already attaches the JWT and handles refresh (see lib/auth.ts).
import { api } from './auth';

export type TeamRole = 'owner' | 'member';

export interface TeamMember {
  id: number;
  user_id: number;
  username: string;
  email: string;
  role: TeamRole;
  created_at: string;
}

export interface TeamMembersResponse {
  team: { id: number; name: string };
  members: TeamMember[];
}

export interface AddMemberResult {
  user: { id: number; username: string; email: string };
  role: TeamRole;
  created: boolean;
  generated_password: string | null;
}

export interface AddMemberPayload {
  email?: string;
  username?: string;
  password?: string;
}

// A team the current user belongs to (a switchable workspace).
export interface TeamSummary {
  team_id: number;
  name: string;
  role: TeamRole;
  owner_id: number;
  owner_username: string;
  is_owner: boolean;
}

// List members of a team. Pass `teamId` to view a specific workspace
// (the active team a member has switched to); omit for the caller's
// default/owned team.
export const listTeamMembers = async (
  teamId?: number | null
): Promise<TeamMembersResponse> =>
  (
    await api.get<TeamMembersResponse>('/api/team/members/', {
      params: teamId ? { team_id: teamId } : undefined,
    })
  ).data;

// Every team the current user belongs to (workspace switcher source).
export const listMyTeams = async (): Promise<TeamSummary[]> =>
  (await api.get<{ teams: TeamSummary[] }>('/api/team/my-teams/')).data.teams;

export const addTeamMember = async (
  payload: AddMemberPayload
): Promise<AddMemberResult> =>
  (await api.post<AddMemberResult>('/api/team/members/', payload)).data;

export const removeTeamMember = async (membershipId: number): Promise<void> => {
  await api.delete(`/api/team/members/${membershipId}/`);
};

export interface CurrentUser {
  id: number;
  username: string;
  email: string;
  is_staff: boolean;
  // Backward-compatible "primary" team (owned team if any, else first).
  team: { id: number; name: string } | null;
  role: TeamRole | null;
  // Full list of teams the user belongs to.
  teams: TeamSummary[];
}

export const getCurrentUser = async (): Promise<CurrentUser> => {
  const data = (await api.get<CurrentUser>('/api/get-user')).data;
  return {
    ...data,
    is_staff: Boolean(data.is_staff),
    teams: data.teams ?? [],
  };
};
